import type { AnalystNoteResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchAnalystNotesApi } from "../../../api/client";
import { analystNotesKeys } from "./analystNotesKeys";
import { type AnalystNote, toAnalystNote } from "./types";

/**
 * Shared queryOptions for a deal's analyst notes (key + queryFn). Backed by the
 * generated `WorkbenchAnalystNotesApi.getAnalystNotes`, which forwards to
 * recap-service and returns the notes newest-first — the table renders them in
 * server order (no client-side sort). Rows are normalized at the boundary via
 * {@link toAnalystNote} so the render path stays free of optional-field handling.
 * `staleTime` matches the app-wide 30s convention.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function analystNotesQuery(approvalId: string) {
  return queryOptions({
    queryKey: analystNotesKeys.list({ approvalId }),
    queryFn: async ({ signal }): Promise<AnalystNote[]> => {
      const response = await workbenchAnalystNotesApi.getAnalystNotes(approvalId, { signal });
      return response.data.map((row: AnalystNoteResponse) => toAnalystNote(row));
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the analyst notes for a deal. Disabled (never fires) until the approval
 * id is present, so callers can render an empty/loading state instead of issuing
 * a doomed request.
 * @param {string} approvalId - The numeric approval / vehicle-loan-application id.
 * @returns {ReturnType<typeof useQuery<AnalystNote[]>>} The TanStack Query result.
 */
export function useAnalystNotes(approvalId: string) {
  return useQuery(analystNotesQuery(approvalId));
}
